/**
 * Correlation ID utilities for request tracing and error tracking.
 * Generates unique identifiers that can be used across frontend and backend
 * to trace requests and errors through the system.
 */

const CORRELATION_ID_KEY = 'x-correlation-id';
const SESSION_CORRELATION_PREFIX = 'session';

/**
 * Generate a unique correlation ID
 * Format: prefix-timestamp-random (e.g., "req-1704067200000-a1b2c3d4")
 */
export function generateCorrelationId(prefix: string = 'req'): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `${prefix}-${timestamp}-${random}`;
}

/**
 * Get or create a session correlation ID
 * Persists across the user session for tracking user journeys
 */
export function getSessionCorrelationId(): string {
  let sessionId = sessionStorage.getItem(CORRELATION_ID_KEY);
  if (!sessionId) {
    sessionId = generateCorrelationId(SESSION_CORRELATION_PREFIX);
    sessionStorage.setItem(CORRELATION_ID_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Create headers with correlation ID for API requests
 */
export function getCorrelationHeaders(): Record<string, string> {
  return {
    [CORRELATION_ID_KEY]: generateCorrelationId(),
    'x-session-id': getSessionCorrelationId()
  };
}

/**
 * Extract correlation ID from error or generate new one
 */
export function getErrorCorrelationId(error?: any): string {
  if (error?.correlationId) return error.correlationId;
  if (error?.response?.headers?.[CORRELATION_ID_KEY]) {
    return error.response.headers[CORRELATION_ID_KEY];
  }
  return generateCorrelationId('err');
}

/**
 * Format error for user display with correlation ID
 */
export function formatUserFriendlyError(
  error: unknown,
  correlationId?: string
): { message: string; correlationId: string } {
  const corrId = correlationId || getErrorCorrelationId(error);
  
  // Never expose technical details in production
  const isProduction = import.meta.env.PROD;
  
  let message = 'An unexpected error occurred. Please try again.';
  
  if (error instanceof Error) {
    // Map known error types to user-friendly messages
    if (error.message.includes('network') || error.message.includes('fetch')) {
      message = 'Unable to connect to the server. Please check your internet connection.';
    } else if (error.message.includes('timeout')) {
      message = 'The request took too long. Please try again.';
    } else if (error.message.includes('unauthorized') || error.message.includes('401')) {
      message = 'Your session has expired. Please sign in again.';
    } else if (error.message.includes('forbidden') || error.message.includes('403')) {
      message = 'You do not have permission to perform this action.';
    } else if (error.message.includes('not found') || error.message.includes('404')) {
      message = 'The requested resource was not found.';
    } else if (error.message.includes('rate limit') || error.message.includes('429')) {
      message = 'Too many requests. Please wait a moment and try again.';
    } else if (!isProduction) {
      // In development, show the actual error message
      message = error.message;
    }
  }
  
  return { message, correlationId: corrId };
}

/**
 * Log error with correlation ID for debugging
 */
export function logErrorWithCorrelation(
  error: unknown,
  context: string,
  additionalData?: Record<string, any>
): string {
  const correlationId = getErrorCorrelationId(error);
  
  console.error(`[${correlationId}] Error in ${context}:`, {
    error,
    correlationId,
    timestamp: new Date().toISOString(),
    sessionId: getSessionCorrelationId(),
    ...additionalData
  });
  
  return correlationId;
}
